import { Form, Input, InputNumber, Select, Switch } from 'antd';
import { useState } from 'react';

import { apiFetch } from '@/api/client';
import { AttributeDataTypeValues, type AttributeDataType } from '@/api/generated/enums';
import {
  itemAttributesKey,
  useItemAttributes,
  type ItemAttribute,
  type ItemAttributeValue,
} from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { FormDrawer } from '@/components/FormDrawer';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { blankToNull, enumOptions, humanizeEnum } from '@core/format/text';
import type { Session } from '@core/session/types';

/*
  The edit case holds an **id**, not the row.

  Holding the row froze it: the drawer's value editor writes through `useApiMutation`, which
  invalidates `itemAttributesKey` and refetches the list — but a row captured into `useState` when
  the pencil was clicked is a snapshot no refetch can reach, so an added value landed in the
  database and never appeared in the list above the form. Resolving the row from the query on every
  render is what makes the drawer show its own writes.
*/
type Editing = { mode: 'create' } | { mode: 'edit'; id: number };

/**
 * The attribute catalogue — the dimensions items vary along
 * (`docs/ItemManagement_Catalogue_Design.md`, step 1).
 *
 * Attributes are the vocabulary a category draws on, not a property of any one item: Size exists
 * once, and Sherwani and Tuxedo both point at it. So this screen owns the *definition* — what an
 * attribute is called and what kind of value it holds — while which categories carry it is decided
 * on the category, and which value a garment actually has is decided on the variant in step 3.
 *
 * The distinction the screen has to make legible is `dataType`. A `List` attribute enumerates its
 * values here and is the only kind that can form a variant; `Number` and `Text` are filled in per
 * item and deliberately have no value list at all. Rendering both as "an attribute" with an empty
 * values section would teach the shop that Chest is simply an attribute nobody has filled in yet.
 */
export function AttributesPage({ session }: { session: Session }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);

  const attributes = useItemAttributes(session.branch?.id, includeInactive);

  const remove = useApiMutation<void, ItemAttribute>({
    mutationFn: (attribute) =>
      apiFetch<void>(`/api/item-attribute/${attribute.id}`, { method: 'DELETE' }),
    success: (_result, attribute) => `${attribute.name} deleted.`,
    invalidates: [itemAttributesKey],
  });

  const columns: DataViewColumn<ItemAttribute>[] = [
    {
      title: 'Attribute',
      dataIndex: 'name',
      primary: true,
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{row.name}</span>
          {/*
            Only branch-local rows are marked, as on the category screen: shared is the ordinary
            case, and badging every row is noise. The exception is what needs spotting.
          */}
          {!row.isShared && <StatusBadge tone="brand">This branch</StatusBadge>}
          {row.inGarmentCode && <StatusBadge tone="neutral">In garment code</StatusBadge>}
          {!row.isActive && <StatusBadge tone="warning">Inactive</StatusBadge>}
        </div>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      width: 150,
      render: (_, row) => <span className="font-mono-nums font-semibold">{row.code}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'dataType',
      width: 130,
      render: (_, row) => (
        <span className="text-default">
          {humanizeEnum(row.dataType)}
          {/* The unit is what makes a Number attribute mean anything — "Chest 98" needs the cm. */}
          {row.unitOfMeasure && <span className="ms-1 text-[11px]">({row.unitOfMeasure})</span>}
        </span>
      ),
    },
    {
      title: 'Values',
      key: 'values',
      width: 220,
      render: (_, row) => <ValuesCell attribute={row} />,
    },
    {
      title: 'Used by',
      dataIndex: 'assignedCategoryCount',
      width: 110,
      align: 'right',
      responsive: ['lg'],
      render: (_, row) => (
        <span className="font-mono-nums text-default">
          {row.assignedCategoryCount === 0
            ? '—'
            : `${row.assignedCategoryCount} categor${row.assignedCategoryCount === 1 ? 'y' : 'ies'}`}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <WriteAction session={session} required="ItemAttribute:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Edit ${row.name}`}
                disabled={disabled}
                onClick={() => setEditing({ mode: 'edit', id: row.id })}
              />
            )}
          </WriteAction>

          {/*
            The server refuses a delete while any category still declares the attribute, and says
            how many. Saying it here too means the rule is learnable without hitting it.
          */}
          <RecordAction
            session={session}
            required="ItemAttribute:Delete"
            forbidden={
              row.assignedCategoryCount > 0
                ? `Used by ${row.assignedCategoryCount} categor${
                    row.assignedCategoryCount === 1 ? 'y' : 'ies'
                  }`
                : undefined
            }
          >
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.name}`}
                title="Delete this attribute?"
                description="Its values go with it."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  if (attributes.isError) {
    return <ApiErrorAlert error={attributes.error} />;
  }

  return (
    <>
      <PageHeading
        title="Attributes"
        subtitle="The dimensions items vary along — size, colour, chest. Categories choose which of them apply."
        actions={
          <>
            <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-default">
              <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />
              Show inactive
            </label>

            <WriteAction session={session} required="ItemAttribute:Create">
              {(disabled) => (
                <Button
                  variant="primary"
                  icon="icon icon-plus"
                  shortcut="create"
                  disabled={disabled}
                  onClick={() => setEditing({ mode: 'create' })}
                >
                  New attribute
                </Button>
              )}
            </WriteAction>
          </>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:pb-1">
        <DataView<ItemAttribute>
          columns={columns}
          dataSource={attributes.data ?? []}
          loading={attributes.isLoading}
          empty={
            <EmptyState
              icon="icon icon-ruler"
              title="No attributes at this branch"
              hint={`Nothing shared company-wide, and nothing local to ${
                session.branch?.code ?? 'this branch'
              }. Start with Size and Colour — they are what variants are combinations of.`}
            />
          }
        />
      </Card>

      {editing && (
        <AttributeDrawer
          session={session}
          mode={editing.mode}
          // Re-resolved from the query every render, which is what lets the drawer see its own
          // writes. `undefined` while the first fetch is in flight, and again if the row goes away
          // underneath — the drawer treats both as "nothing to edit yet" rather than crashing.
          existing={
            editing.mode === 'edit'
              ? (attributes.data ?? []).find((row) => row.id === editing.id)
              : undefined
          }
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

/**
 * The values, as a preview rather than a count.
 *
 * "4 values" tells an operator nothing they wanted to know; "38, 40, 42, +1" tells them whether the
 * list is the one they meant. A `Number` or `Text` attribute has no list *by design*, so it says so
 * rather than showing an empty one — the difference between "none defined yet" and "never has any"
 * is the whole point of the data type.
 */
function ValuesCell({ attribute }: { attribute: ItemAttribute }) {
  if (attribute.dataType !== 'List') {
    return <span className="text-[12px] text-default">Entered per item</span>;
  }

  if (attribute.values.length === 0) {
    return <span className="text-[12px] text-warning">No values yet</span>;
  }

  const shown = attribute.values.slice(0, 4);
  const rest = attribute.values.length - shown.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((value) => (
        <span
          key={value.id}
          className="inline-flex items-center gap-1 rounded bg-light px-1.5 py-0.5 text-[11px] text-default"
        >
          {value.colourHex && (
            <span
              className="size-2.5 rounded-full border border-border-color"
              style={{ background: value.colourHex }}
              aria-hidden="true"
            />
          )}
          {value.name}
        </span>
      ))}
      {rest > 0 && <span className="text-[11px] text-default">+{rest}</span>}
    </div>
  );
}

interface AttributeFormValues {
  code: string;
  name: string;
  /** Create only — scope is fixed at creation, as it is for a category and an item. */
  shared?: boolean;
  dataType: AttributeDataType;
  description?: string | null;
  unitOfMeasure?: string | null;
  sortOrder: number;
  isActive: boolean;
  /** Unset on a new attribute means "the usual": the server marks SIZE and nothing else. */
  inGarmentCode?: boolean;
}

function AttributeDrawer({
  session,
  mode,
  existing,
  onClose,
}: {
  session: Session;
  mode: Editing['mode'];
  /** The live row from the query, re-resolved by the caller each render. */
  existing: ItemAttribute | undefined;
  onClose: () => void;
}) {
  const [form] = Form.useForm<AttributeFormValues>();
  const isEdit = mode === 'edit';

  // Watched rather than read from `existing`, because the fields below have to follow what is
  // *currently* selected in the form — a user switching Size from List to Number should see the
  // values section go away before they save, not after.
  const dataType = Form.useWatch('dataType', form) ?? existing?.dataType ?? 'List';

  // Until somebody touches the switch on a new attribute, it shows what the server will decide —
  // on for SIZE, off otherwise — so the screen never says one thing and the save another.
  const typedCode = Form.useWatch('code', form) ?? '';

  const save = useApiMutation<ItemAttribute, AttributeFormValues>({
    mutationFn: (raw) => {
      const values = blankToNull(raw);

      return isEdit
        ? apiFetch<ItemAttribute>(`/api/item-attribute/${existing?.id}`, {
            method: 'PUT',
            body: values,
          })
        : apiFetch<ItemAttribute>('/api/item-attribute', { method: 'POST', body: values });
    },
    success: isEdit ? 'Attribute updated.' : 'Attribute created.',
    invalidates: [itemAttributesKey],
    onSuccess: onClose,
  });

  const hasValues = (existing?.values.length ?? 0) > 0;

  return (
    <FormDrawer<AttributeFormValues>
      title={isEdit ? `Edit ${existing?.name ?? 'attribute'}` : 'New attribute'}
      open
      onClose={onClose}
      width={620}
      form={form}
      initialValues={
        existing ?? {
          code: '',
          name: '',
          shared: false,
          dataType: 'List',
          sortOrder: 0,
          isActive: true,
        }
      }
      onSubmit={(values) => void submitVia(save, values)}
      submitText={isEdit ? 'Save changes' : 'Create attribute'}
      submitting={save.isPending}
    >
      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item
          name="code"
          label="Code"
          rules={[{ required: true, message: 'A code is required' }]}
          extra={
            isEdit
              ? 'Fixed at creation — variant signatures and imports refer to it'
              : 'Upper case, e.g. SIZE. Fixed once created.'
          }
        >
          <Input disabled={isEdit} className="font-mono-nums" />
        </Form.Item>

        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'A name is required' }]}
        >
          <Input />
        </Form.Item>
      </div>

      {/*
        Create only, exactly as on the category and item forms: narrowing a shared attribute would
        hide it at every other branch, where a category may already declare it.
      */}
      {!isEdit && (
        <Form.Item
          name="shared"
          label="Shared across all branches"
          valuePropName="checked"
          extra="On: every branch sees this attribute. Off: only the branch you are working at. Sharing needs a company-wide role."
        >
          <Switch size="small" />
        </Form.Item>
      )}

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item
          name="dataType"
          label="Kind of value"
          rules={[{ required: true, message: 'Pick what kind of value this holds' }]}
          extra={
            hasValues
              ? 'Fixed while values exist — switching would strand them.'
              : 'Only a list attribute can form variants.'
          }
        >
          <Select options={enumOptions(AttributeDataTypeValues)} disabled={hasValues} />
        </Form.Item>

        {/*
          Only a Number attribute has a unit. Offering the field on a list attribute invites "cm"
          against Colour, which is a value nothing will ever read.
        */}
        {dataType === 'Number' && (
          <Form.Item
            name="unitOfMeasure"
            label="Measured in"
            extra="cm, inches — what the number means."
          >
            <Input />
          </Form.Item>
        )}
      </div>

      <Form.Item name="description" label="Description">
        <Input.TextArea rows={2} placeholder="What this attribute records." />
      </Form.Item>

      <div className="flex items-center gap-8">
        <Form.Item name="sortOrder" label="Sort order">
          <InputNumber precision={0} className="w-full" />
        </Form.Item>

        {isEdit && (
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch size="small" />
          </Form.Item>
        )}
      </div>

      {/* Only a list attribute has values to put in a code. */}
      {dataType === 'List' && (
        <Form.Item
          name="inGarmentCode"
          label="Goes into garment codes"
          valuePropName="checked"
          getValueProps={(value: boolean | undefined) => ({
            checked: value ?? (!isEdit && typedCode.trim().toUpperCase() === 'SIZE'),
          })}
          extra="On for Size: a size-36 Black Jodhpuri Bandhgala is BJB-36-01. Turn it on for anything else that should appear in the code, such as Fit. Garments already tagged keep their codes."
        >
          <Switch size="small" />
        </Form.Item>
      )}

      {dataType === 'List' && (
        <div className="border-t border-border-color pt-4">
          <h3 className="text-[13px] font-semibold text-gray-900">Values</h3>
          <p className="mt-0.5 mb-3 text-[12px] text-default">
            What can be chosen for this attribute. In step 3 a variant is one value from each
            variant-forming attribute.
          </p>

          {existing ? (
            <ValueEditor session={session} attribute={existing} />
          ) : (
            // Values hang off an attribute id, and IDENTITY keys do not exist until the insert has
            // run — so there is genuinely nothing to attach them to yet. Said plainly rather than
            // by showing an editor that would fail on submit.
            <Notice title="Save the attribute first">
              Values are added once it exists. Create it, then reopen to fill in the list.
            </Notice>
          )}
        </div>
      )}
    </FormDrawer>
  );
}

interface ValueDraft {
  code: string;
  name: string;
  sortOrder: number;
  colourHex: string;
  numericValue: number | null;
  rateAdjustmentPct: number | null;
}

const emptyDraft: ValueDraft = {
  code: '',
  name: '',
  sortOrder: 0,
  colourHex: '',
  numericValue: null,
  rateAdjustmentPct: null,
};

/**
 * The value list, edited in place inside the attribute drawer.
 *
 * Deliberately **not** an Ant Design `Form`: the drawer already renders one, and a nested `<form>`
 * is invalid HTML whose inner submit button submits the outer form. So these are controlled inputs
 * with their own draft state, and every write goes through the same `useApiMutation` path the rest
 * of the app uses.
 *
 * Every endpoint here returns the whole attribute rather than the changed value, so the list is
 * redrawn from the server's answer and never from a local guess about what the write did.
 */
function ValueEditor({ session, attribute }: { session: Session; attribute: ItemAttribute }) {
  const [draft, setDraft] = useState<ValueDraft>(emptyDraft);
  const [editingId, setEditingId] = useState<number | null>(null);

  // A swatch is only meaningful on a colour attribute, and the shop names its own codes — so this
  // is a guess from the code rather than a flag on the table. Both spellings, because a shop that
  // typed COLOR should not lose the swatch over it.
  const upperCode = attribute.code.toUpperCase();
  const isColour = upperCode.includes('COLOUR') || upperCode.includes('COLOR');

  const add = useApiMutation<ItemAttribute, ValueDraft>({
    mutationFn: (value) =>
      apiFetch<ItemAttribute>(`/api/item-attribute/${attribute.id}/values`, {
        method: 'POST',
        body: blankToNull(value),
      }),
    success: 'Value added.',
    invalidates: [itemAttributesKey],
    onSuccess: () => setDraft(emptyDraft),
  });

  const update = useApiMutation<ItemAttribute, { id: number; value: ValueDraft }>({
    mutationFn: ({ id, value }) =>
      apiFetch<ItemAttribute>(`/api/item-attribute/${attribute.id}/values/${id}`, {
        method: 'PUT',
        body: blankToNull(value),
      }),
    success: 'Value updated.',
    invalidates: [itemAttributesKey],
    onSuccess: () => {
      setEditingId(null);
      setDraft(emptyDraft);
    },
  });

  const remove = useApiMutation<ItemAttribute, ItemAttributeValue>({
    mutationFn: (value) =>
      apiFetch<ItemAttribute>(`/api/item-attribute/${attribute.id}/values/${value.id}`, {
        method: 'DELETE',
      }),
    success: (_result, value) => `${value.name} removed.`,
    invalidates: [itemAttributesKey],
  });

  const startEdit = (value: ItemAttributeValue) => {
    setEditingId(value.id);
    setDraft({
      code: value.code,
      name: value.name,
      sortOrder: value.sortOrder,
      colourHex: value.colourHex ?? '',
      numericValue: value.numericValue,
      rateAdjustmentPct: value.rateAdjustmentPct,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const submit = () => {
    if (draft.code.trim() === '' || draft.name.trim() === '') {
      return;
    }

    if (editingId === null) {
      void add.mutateAsync(draft).catch(() => undefined);
    } else {
      void update.mutateAsync({ id: editingId, value: draft }).catch(() => undefined);
    }
  };

  const pending = add.isPending || update.isPending;

  return (
    <div>
      {attribute.values.length > 0 && (
        <ul className="mb-3 divide-y divide-border-color rounded-md border border-border-color">
          {attribute.values.map((value) => (
            <li key={value.id} className="flex items-center gap-2 px-3 py-2">
              {value.colourHex && (
                <span
                  className="size-4 shrink-0 rounded border border-border-color"
                  style={{ background: value.colourHex }}
                  aria-hidden="true"
                />
              )}

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[13px] font-medium text-gray-900">
                    {value.name}
                  </span>
                  {!value.isActive && <StatusBadge tone="warning">Inactive</StatusBadge>}
                </div>
                <span className="font-mono-nums text-[11px] text-default">
                  {value.code}
                  {value.numericValue !== null && ` · ${value.numericValue}`}
                  {/*
                    A rate modifier is the one thing here that changes what a customer pays, so it
                    is shown on the row rather than only in the edit form.
                  */}
                  {value.rateAdjustmentPct !== null &&
                    value.rateAdjustmentPct !== 0 &&
                    ` · ${value.rateAdjustmentPct > 0 ? '+' : ''}${value.rateAdjustmentPct}%`}
                </span>
              </div>

              <div className="flex shrink-0 gap-1">
                <WriteAction session={session} required="ItemAttribute:Edit">
                  {(disabled) => (
                    <IconAction
                      icon="icon icon-pencil-line"
                      label={`Edit ${value.name}`}
                      disabled={disabled || pending}
                      onClick={() => startEdit(value)}
                    />
                  )}
                </WriteAction>

                <WriteAction session={session} required="ItemAttribute:Delete">
                  {(disabled) => (
                    <ConfirmButton
                      icon="icon icon-trash-2"
                      label={`Remove ${value.name}`}
                      title="Remove this value?"
                      confirmText="Remove"
                      disabled={disabled}
                      pending={remove.isPending}
                      onConfirm={() => void remove.mutateAsync(value).catch(() => undefined)}
                    />
                  )}
                </WriteAction>
              </div>
            </li>
          ))}
        </ul>
      )}

      <WriteAction session={session} required="ItemAttribute:Edit">
        {(disabled) => (
          <div className="rounded-md border border-border-color bg-light p-3">
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-[12px] text-default">Code</span>
                <Input
                  value={draft.code}
                  // Fixed at creation on the server, for the reason the attribute's is: variant
                  // signatures store it. Editable only while adding.
                  disabled={disabled || editingId !== null}
                  className="font-mono-nums"
                  placeholder="40"
                  onChange={(event) => setDraft({ ...draft, code: event.target.value })}
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-[12px] text-default">Name</span>
                <Input
                  value={draft.name}
                  disabled={disabled}
                  placeholder="40 inch"
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </label>
            </div>

            <div className="mt-2 grid grid-cols-3 gap-2">
              <label className="block">
                <span className="mb-1 block text-[12px] text-default">Sorts as</span>
                <InputNumber
                  value={draft.numericValue}
                  disabled={disabled}
                  className="w-full"
                  placeholder="40"
                  onChange={(value) => setDraft({ ...draft, numericValue: value ?? null })}
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-[12px] text-default">Rate change</span>
                <InputNumber
                  value={draft.rateAdjustmentPct}
                  disabled={disabled}
                  className="w-full"
                  addonAfter="%"
                  precision={2}
                  onChange={(value) => setDraft({ ...draft, rateAdjustmentPct: value ?? null })}
                />
              </label>

              {/*
                Only offered where it means something. A hex on Size is a value no screen reads,
                and the swatch it would draw is worse than no swatch.
              */}
              {isColour && (
                <label className="block">
                  <span className="mb-1 block text-[12px] text-default">Swatch</span>
                  <Input
                    value={draft.colourHex}
                    disabled={disabled}
                    placeholder="#1E293B"
                    className="font-mono-nums"
                    onChange={(event) => setDraft({ ...draft, colourHex: event.target.value })}
                  />
                </label>
              )}
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
              {editingId !== null && (
                <Button onClick={cancelEdit} disabled={pending}>
                  Cancel
                </Button>
              )}

              <Button
                variant="primary"
                icon={editingId === null ? 'icon icon-plus' : 'icon icon-check'}
                loading={pending}
                disabled={
                  disabled || pending || draft.code.trim() === '' || draft.name.trim() === ''
                }
                onClick={submit}
              >
                {editingId === null ? 'Add value' : 'Save value'}
              </Button>
            </div>
          </div>
        )}
      </WriteAction>
    </div>
  );
}
